/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import TWEEN from '@tweenjs/tween.js'

import Alert from '../../components/Alert'

import SimpleObject from './SimpleObject'
import { ARConfig } from './TokenPlugin'
import { ARViewerWeb } from '.'

declare let THREE: any

// Reusable vars
const v1 = new THREE.Vector3()
const yAxis = new THREE.Vector3(0, 1, 0)

/** The distance from the camera that AR objects are placed */
const DistanceFromCamera = 5

/** The starting height, on the Y axis */
const ObjectBaseHeight = 2

/** Responsible for loading and displaying tokens in the 3D environment */
export default class SimpleObjectPlugin {
  ar: ARViewerWeb
  // region?: Region;
  modelObjects: SimpleObject[]
  // VisibleRadius: any
  // distanceMultiplier: any
  // minimumDistance = 1;
  // additionalRandomDistance = 5;
  actionCalled: boolean
  numLoading: number
  v1: any
  loadPromiseResolver: any
  loadPromiseFailer: ((reason?: any) => void) | null

  constructor(ar: ARViewerWeb) {
    // Store AR
    this.ar = ar
    this.ar.simpleObjectPlugin = this

    this.modelObjects = []

    this.actionCalled = false

    // Number of loading objects
    this.numLoading = 0

    // Vars used for doing calculations
    this.v1 = new THREE.Vector3()

    // Start updating tween lib. This must only happen once, even if multiple components use the Tween library.
    if (!(window as any).tweenIsRunning) {
      ;(window as any).tweenIsRunning = true
      const animate = function (time: number) {
        requestAnimationFrame(animate)
        TWEEN.update(time)
      }
      requestAnimationFrame(animate)
    }

    this.loadPromiseFailer = (err: any) => {
      throw new Error('Method not implemented.')
    }
  }

  get name() {
    return 'TokenPlugin'
  }

  get config(): ARConfig {
    return {
      maxItems: 5,
      resizeObjects: true,
      rearrangeObjects: true,
      desiredRadius: 1,
      scalingStrategy: 'new',
      distanceFromCamera: 5,
      objectBaseHeight: 2,
      minimumDistance: 1,
      additionalRandomDistance: 5,
      // ...Config.features?.ar,
      ...this.ar.customSession?.config
    }
  }

  async load(div: HTMLDivElement) {
    if (!this.ar.customSession?.config.modelUrl) return

    // Load started
    console.debug(
      `[AR] SimpleObjectPlugin loading: maxItems=${this.config.maxItems || 2} minDistance=${
        this.config.minimumDistance
      } additionalRandomDistance=${this.config.additionalRandomDistance} maxPoints=${
        this.config.maxPoints
      } userPoints=${this.config.userPoints}`
    )

    for (let i = 0; i < this.config.maxPoints - this.config.userPoints; i++) {
      const modelObject = new SimpleObject(
        this.ar.customSession.config.modelUrl!,
        this.ar.scene,
        this.ar.renderer,
        this.ar.audioListener
      )
      this.modelObjects.push(modelObject)

      this.ar.scene.add(modelObject.container)

      modelObject.onClick = this.onModelClick.bind(this, modelObject)

      // Add action handler
      modelObject.tokenActionPerformedListener = (e: any) => {
        // Notify all plugins
        for (const plugin of this.ar.plugins) if (plugin.onTokenAction) plugin.onTokenAction(e)
      }

      modelObject.load().catch((err: any) => {
        // Failed to load!
        console.warn('Failed to load Token in AR view!', err)
      })

      // Move to appropriate position
      const newPosition = v1
      newPosition.z = this.config.minimumDistance

      newPosition.normalize()

      // New scaling strategy
      newPosition.multiplyScalar(
        this.config.minimumDistance + Math.random() * this.config.additionalRandomDistance
      )

      // Calculate random height
      const randomHeightFrom = 1
      const randomHeightTo = 2
      newPosition.y = Math.random() * (randomHeightTo - randomHeightFrom) + randomHeightFrom

      // Randomly pick a spot
      newPosition.applyAxisAngle(yAxis, Math.PI * 2 * Math.random())

      // Set position immediately
      modelObject.container.position.copy(newPosition)
      console.log('[AR] Setting position:', modelObject.container.position)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  unload() {}

  /** Show debug stats */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  debugStats() {}

  /** Called when the user clicks on a token object */
  async onModelClick(modelObject: SimpleObject) {
    const actionName = 'varius.action:varius.io:collect-game-token-v1'
    // Catch errors
    try {
      // Perform action
      this.ar.customSession?.token?.performAction(actionName)

      // Log analytics
      // Analytics.event(
      // 	"performAction",
      // 	{
      // 		event: "performAction",
      // 		actionUri: actionName,
      // 		eventValue: 1,
      // 	},
      // 	this.ar.customSession?.token
      // );
    } catch (err: any) {
      // Show error
      alert('Error: ' + err.message)
      // Errors.show(err);
    }

    if (modelObject.tokenActionPerformedListener && actionName)
      modelObject.tokenActionPerformedListener({
        token: this.ar.customSession?.token,
        actionName, // Assume this is a Pickup action for now
        actionData: {}
      })

    // No longer in array, remove it!
    for (let i = 0; i < this.modelObjects.length; i++) {
      if (modelObject.id === this.modelObjects[i].id) {
        this.ar.scene.remove(modelObject.container)
        this.modelObjects.splice(i--, 1)
      }
    }
  }

  /** Called every frame */
  onRender3D(delta: any, scene: any, camera: any) {
    // Notify objects
    for (const obj of this.modelObjects) obj.onRender3D(delta, scene, camera)
  }
}
